import { fetchSwissHeight } from 'api/otherApi';

interface BorderCoordinate {
  x?: number | string;
  y?: number | string;
  z?: number | string;
  [key: string]: unknown;
}

interface SwissHeightPayload {
  height?: number | string;
  elevation?: number | string;
  altitude?: number | string;
  h?: number | string;
  alts?: {
    COMB?: number | string;
    DTM2?: number | string;
    DTM25?: number | string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface GridPayloadParams {
  [key: string]: unknown;
}

const heightCache = new Map<string, number | Promise<number>>();

const getCoordinateCacheKey = (coordinate: BorderCoordinate = {}) => {
  const x = Number(coordinate?.x);
  const y = Number(coordinate?.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    return null;
  }
  return `${x.toFixed(2)}:${y.toFixed(2)}`;
};

const normalizeMultiPolygon = (value: unknown): BorderCoordinate[][] => {
  if (!Array.isArray(value) || value.length === 0) return [];
  return Array.isArray(value[0]) ? (value as BorderCoordinate[][]) : [value as BorderCoordinate[]];
};

const parseBorderCoordinates = (value: unknown): BorderCoordinate[][] => {
  if (Array.isArray(value)) return normalizeMultiPolygon(value);

  if (!value || typeof value !== 'string') {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    return normalizeMultiPolygon(parsed);
  } catch (error) {
    return [];
  }
};

const extractHeightValue = (payload: SwissHeightPayload = {}) => {
  const candidate = [
    payload?.height,
    payload?.elevation,
    payload?.altitude,
    payload?.h,
    payload?.alts?.COMB,
    payload?.alts?.DTM2,
    payload?.alts?.DTM25,
  ].find((value) => Number.isFinite(Number(value)));
  return Number.isFinite(Number(candidate)) ? Number(candidate) : 0;
};

const getHeightForLv95Coordinate = async (coordinate: BorderCoordinate = {}) => {
  const x = Number(coordinate?.x);
  const y = Number(coordinate?.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    return 0;
  }

  const cacheKey = getCoordinateCacheKey({ x, y });
  if (!cacheKey) {
    return 0;
  }

  const cached = heightCache.get(cacheKey);
  if (cached !== undefined) {
    return await cached;
  }

  const requestPromise = (async () => {
    try {
      const response = await fetchSwissHeight(x, y);
      if (!response.ok) {
        return 0;
      }
      const payload = await response.json();
      return extractHeightValue(payload);
    } catch (error) {
      return 0;
    }
  })();

  heightCache.set(cacheKey, requestPromise);
  const resolved = await requestPromise;
  heightCache.set(cacheKey, resolved);
  return resolved;
};

export const enrichBorderCoordinatesWithHeight = async (coordinates: BorderCoordinate[] = []) => {
  if (!Array.isArray(coordinates) || coordinates.length === 0) {
    return coordinates;
  }

  const uniqueCoordinates = new Map<string, BorderCoordinate>();
  coordinates.forEach((coordinate) => {
    const key = getCoordinateCacheKey(coordinate);
    if (!key) {
      return;
    }
    if (!uniqueCoordinates.has(key)) {
      uniqueCoordinates.set(key, coordinate);
    }
  });

  const heightEntries = await Promise.allSettled(
    Array.from(uniqueCoordinates.entries()).map(async ([key, coordinate]): Promise<[string, number]> => {
      const z = await getHeightForLv95Coordinate(coordinate);
      return [key, z];
    })
  );

  const zByCoordinate = new Map<string, number>();
  heightEntries.forEach((entry) => {
    if (entry.status === 'fulfilled') {
      const [key, z] = entry.value;
      zByCoordinate.set(key, z);
    }
  });

  return coordinates.map((coordinate) => {
    const key = getCoordinateCacheKey(coordinate);
    const existingZ = Number(coordinate?.z);
    if (Number.isFinite(existingZ)) {
      return coordinate;
    }
    if (!key || !zByCoordinate.has(key)) {
      return coordinate;
    }
    return { ...coordinate, z: zByCoordinate.get(key) };
  });
};

const enrichPayloadBorder = async (gridPayloadParams: GridPayloadParams, key: string) => {
  if (!Object.prototype.hasOwnProperty.call(gridPayloadParams, key)) {
    return undefined;
  }

  const originalValue = gridPayloadParams[key];
  if (originalValue == null) {
    return originalValue;
  }

  const parsedCoordinates = parseBorderCoordinates(originalValue);
  if (!Array.isArray(parsedCoordinates) || parsedCoordinates.length === 0) {
    return originalValue;
  }

  const enrichedCoordinates = await Promise.all(
    parsedCoordinates.map((polygon) => enrichBorderCoordinatesWithHeight(polygon))
  );
  return typeof originalValue === 'string' ? JSON.stringify(enrichedCoordinates) : enrichedCoordinates;
};

export const enrichGridParamsPayloadWithHeight = async (gridPayloadParams: GridPayloadParams = {}) => {
  const keys = ['legalBorder', 'buildableBorder', 'analysisArea'];
  const enrichedEntries = await Promise.all(
    keys.map(async (key): Promise<[string, unknown]> => {
      const enrichedValue = await enrichPayloadBorder(gridPayloadParams, key);
      return [key, enrichedValue];
    })
  );

  return enrichedEntries.reduce<GridPayloadParams>(
    (payload, [key, enrichedValue]) => {
      if (enrichedValue !== undefined) {
        payload[key] = enrichedValue;
      }
      return payload;
    },
    {
      ...gridPayloadParams,
    }
  );
};
